<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import type { Component } from 'vue';
import {
  CircleCheckBig,
  Home,
  Info,
  Building2,
  FileCheck,
  BookOpen,
  Megaphone,
  GraduationCap,
  Briefcase,
  Users,
  Quote,
} from 'lucide-vue-next';
import * as LucideIcons from 'lucide-vue-next';
import { useContentStore } from '@/stores/content';
import type { ApiImage } from '@/types/images';
import { buildSrcSet, isOptimisedImage } from '@/types/images';
import { onOfflineMediaImgError } from '@/utils/offlineMediaPlaceholder';

const BuildingIcon = (LucideIcons as Record<string, Component>).Building;
const MessageSquareIcon =
  (LucideIcons as Record<string, Component>).MessageSquare;
const FileTextIcon =
  (LucideIcons as Record<string, Component>).FileText ??
  (LucideIcons as Record<string, Component>).FileTextIcon;
const ArrowRightIcon =
  (LucideIcons as Record<string, Component>).ArrowRight ??
  (LucideIcons as Record<string, Component>).ArrowRightIcon;
const DEFAULT_PAGE_ICON: Component = FileTextIcon ?? FileCheck;

const PAGE_LINK_ICON_MAP: Record<string, Component> = {
  'circle-check-big': CircleCheckBig,
  home: Home,
  info: Info,
  building: BuildingIcon ?? Building2,
  'building-2': Building2,
  'file-check': FileCheck,
  'file-text': DEFAULT_PAGE_ICON,
  'book-open': BookOpen,
  'message-square': MessageSquareIcon ?? Info,
  megaphone: Megaphone,
  'graduation-cap': GraduationCap,
  briefcase: Briefcase,
  users: Users,
  quote: Quote,
};

function iconForName(iconName: string): Component | null {
  return PAGE_LINK_ICON_MAP[iconName] ?? null;
}

type ThreePageLinkEntry = {
  page?: number | null;
  description?: string | null;
  cta_label?: string | null;
  variant?: string | null;
};

const props = defineProps<{
  links?: ThreePageLinkEntry[] | null;
  /** Block-picker iframe: JSON array of { title, description?, ctaLabel?, variant?, icon? }. */
  preview_cards?: string | null;
}>();

const contentStore = useContentStore();

const DEFAULT_CTA = 'Learn more';

type ResolvedCard = {
  pageId: number;
  url: string;
  title: string;
  description: string;
  ctaLabel: string;
  variant: 'primary' | 'secondary' | 'accent';
  image: ApiImage;
  iconComponent: Component;
};

const resolvedCards = computed((): ResolvedCard[] => {
  const raw = props.links;
  if (!Array.isArray(raw) || !raw.length) return [];

  const out: ResolvedCard[] = [];
  for (const entry of raw) {
    const pageId = entry.page;
    if (pageId == null) continue;
    const pageItem = contentStore.getPageById(pageId);
    if (!pageItem) continue;

    const blockDesc = entry.description?.trim();
    const description =
      blockDesc ||
      pageItem.meta?.search_description?.trim() ||
      '';

    const ctaRaw = entry.cta_label?.trim();
    const ctaLabel = ctaRaw || DEFAULT_CTA;

    let variant: ResolvedCard['variant'] = 'primary';
    const v = (entry.variant || 'primary').trim();
    if (v === 'secondary' || v === 'accent') variant = v;

    const iconName = pageItem.meta?.icon?.trim();
    const fromMeta = iconName ? iconForName(iconName) : null;
    const iconComponent = fromMeta ?? DEFAULT_PAGE_ICON;

    const image = (pageItem.image ?? null) as ApiImage | null;

    out.push({
      pageId,
      url: contentStore.getUrlForPageId(pageId),
      title: pageItem.title,
      description,
      ctaLabel,
      variant,
      image,
      iconComponent,
    });
  }
  return out;
});

const staticPreviewCards = computed((): ResolvedCard[] => {
  const raw = props.preview_cards?.trim();
  if (!raw) return [];
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(data)) return [];
  const out: ResolvedCard[] = [];
  let i = 0;
  for (const row of data) {
    if (!row || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    const title = String(r.title ?? '').trim();
    if (!title) continue;
    let variant: ResolvedCard['variant'] = 'primary';
    const v = String(r.variant ?? 'primary').trim();
    if (v === 'secondary' || v === 'accent') variant = v;
    const iconName = String(r.icon ?? '').trim();
    const fromMeta = iconName ? iconForName(iconName) : null;
    const iconComponent = fromMeta ?? DEFAULT_PAGE_ICON;
    out.push({
      pageId: -1000 - i,
      url: '/',
      title,
      description: String(r.description ?? '').trim(),
      ctaLabel: String(r.ctaLabel ?? DEFAULT_CTA).trim() || DEFAULT_CTA,
      variant,
      image: null,
      iconComponent,
    });
    i += 1;
  }
  return out;
});

const isStaticPreview = computed(() => staticPreviewCards.value.length > 0);

const displayedCards = computed((): ResolvedCard[] =>
  isStaticPreview.value ? staticPreviewCards.value : resolvedCards.value,
);

function cardRootAttrs(card: ResolvedCard) {
  const base = [
    'three-page-link-block__card',
    `three-page-link-block__card--${card.variant}`,
  ];
  if (isStaticPreview.value) {
    return {
      role: 'article' as const,
      class: [...base, 'three-page-link-block__card--preview'],
    };
  }
  return {
    to: card.url,
    class: base,
  };
}

function legacyImageUrl(img: ApiImage): string | null {
  return img && 'url' in img && img.url ? img.url : null;
}
</script>

<template>
  <div
    v-if="displayedCards.length"
    class="three-page-link-block"
    :class="{ 'three-page-link-block--preview': isStaticPreview }"
  >
    <div class="three-page-link-block__grid">
      <component
        :is="isStaticPreview ? 'div' : RouterLink"
        v-for="card in displayedCards"
        :key="card.pageId"
        v-bind="cardRootAttrs(card)"
      >
        <div
          v-if="card.image"
          class="three-page-link-block__media"
        >
          <div
            class="three-page-link-block__media-gradient"
            aria-hidden="true"
          />
          <picture v-if="isOptimisedImage(card.image)">
            <source
              v-for="source in card.image.sources"
              :key="source.media_type"
              :type="source.media_type"
              :srcset="buildSrcSet(source.src_set)"
            />
            <img
              :src="card.image.fallback!.url"
              :srcset="buildSrcSet(card.image.fallback!.src_set)"
              :alt="card.title"
              class="three-page-link-block__media-img"
              loading="lazy"
              @error="onOfflineMediaImgError"
            />
          </picture>
          <img
            v-else-if="legacyImageUrl(card.image)"
            :src="legacyImageUrl(card.image)!"
            :alt="card.title"
            class="three-page-link-block__media-img"
            loading="lazy"
            @error="onOfflineMediaImgError"
          />
        </div>

        <div class="three-page-link-block__body">
          <div class="three-page-link-block__intro">
            <div class="three-page-link-block__icon-wrap">
              <component
                :is="card.iconComponent"
                class="three-page-link-block__icon"
                aria-hidden="true"
              />
            </div>
            <h3 class="three-page-link-block__title">
              {{ card.title }}
            </h3>
          </div>
          <p
            v-if="card.description"
            class="three-page-link-block__description"
          >
            {{ card.description }}
          </p>
          <span class="three-page-link-block__cta">
            <span class="three-page-link-block__cta-text">{{ card.ctaLabel }}</span>
            <component
              :is="ArrowRightIcon"
              class="three-page-link-block__cta-arrow"
              aria-hidden="true"
            />
          </span>
        </div>
      </component>
    </div>
  </div>
</template>

<style scoped>
.three-page-link-block {
  width: 100%;
}

.three-page-link-block__card--preview {
  cursor: default;
}

.three-page-link-block__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

@media (min-width: 768px) {
  .three-page-link-block__grid {
    grid-template-columns: repeat(auto-fit, minmax(16rem, 1fr));
    gap: 2rem;
  }
}

.three-page-link-block__card {
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 0.5rem;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  text-align: left;
  transition: border-color 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease;
  min-height: 0;
}

.three-page-link-block__card:hover {
  box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
  transform: translateY(-4px);
}

.three-page-link-block__card--primary:hover {
  border-color: rgba(4, 120, 87, 0.35);
}

.three-page-link-block__card--secondary:hover {
  border-color: rgba(124, 58, 237, 0.35);
}

.three-page-link-block__card--accent:hover {
  border-color: rgba(234, 88, 12, 0.35);
}

.three-page-link-block__media {
  position: relative;
  width: 100%;
  height: 12rem;
  overflow: hidden;
  flex-shrink: 0;
}

.three-page-link-block__media-gradient {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.4) 0%,
    rgba(0, 0, 0, 0.08) 45%,
    transparent 100%
  );
  pointer-events: none;
}

.three-page-link-block__media-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.three-page-link-block__card:hover .three-page-link-block__media-img {
  transform: scale(1.05);
}

.three-page-link-block__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  min-height: 0;
  gap: 0.75rem;
}

.three-page-link-block__intro {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.three-page-link-block__icon-wrap {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.375rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background-color 0.3s ease;
}

.three-page-link-block__card--primary .three-page-link-block__icon-wrap {
  background-color: rgba(4, 120, 87, 0.1);
}

.three-page-link-block__card--secondary .three-page-link-block__icon-wrap {
  background-color: rgba(124, 58, 237, 0.1);
}

.three-page-link-block__card--accent .three-page-link-block__icon-wrap {
  background-color: rgba(234, 88, 12, 0.1);
}

.three-page-link-block__card--primary:hover .three-page-link-block__icon-wrap {
  background-color: rgba(4, 120, 87, 0.18);
}

.three-page-link-block__card--secondary:hover .three-page-link-block__icon-wrap {
  background-color: rgba(124, 58, 237, 0.18);
}

.three-page-link-block__card--accent:hover .three-page-link-block__icon-wrap {
  background-color: rgba(234, 88, 12, 0.18);
}

.three-page-link-block__icon {
  width: 1.25rem;
  height: 1.25rem;
}

.three-page-link-block__card--primary .three-page-link-block__icon {
  color: #047857;
}

.three-page-link-block__card--secondary .three-page-link-block__icon {
  color: #7c3aed;
}

.three-page-link-block__card--accent .three-page-link-block__icon {
  color: #ea580c;
}

.three-page-link-block__title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.25;
  transition: color 0.3s ease;
}

@media (min-width: 640px) {
  .three-page-link-block__title {
    font-size: 1.25rem;
  }
}

.three-page-link-block__card--primary:hover .three-page-link-block__title {
  color: #047857;
}

.three-page-link-block__card--secondary:hover .three-page-link-block__title {
  color: #7c3aed;
}

.three-page-link-block__card--accent:hover .three-page-link-block__title {
  color: #ea580c;
}

.three-page-link-block__description {
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.6;
  color: var(--color-text-muted, #525252);
  flex: 1;
}

.three-page-link-block__cta {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.25rem;
  font-size: 1rem;
  font-weight: 600;
  transition: color 0.3s ease, gap 0.2s ease;
}

.three-page-link-block__card--primary .three-page-link-block__cta {
  color: #047857;
}

.three-page-link-block__card--secondary .three-page-link-block__cta {
  color: #7c3aed;
}

.three-page-link-block__card--accent .three-page-link-block__cta {
  color: #ea580c;
}

.three-page-link-block__cta-arrow {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.three-page-link-block__card:hover .three-page-link-block__cta-arrow {
  transform: translateX(4px);
}
</style>
